/* ==========================================================================
   slider.js — reimplementación nativa del slider de PageFly
   --------------------------------------------------------------------------
   Cubre los tres tipos de carrusel del <main>:
     1. Slideshow de portada  (.pf-slideshow > .pf-slider)      autoplay + dots
     2. Listas horizontales   (.pf-content-list* / .pf-product-list > .pf-slider)
     3. Galería de la tarjeta de producto (.pf-media-slider)

   Modelo idéntico al original: el contenedor es un scroller horizontal con
   `scroll-snap-type: x mandatory` y `scroll-behavior: smooth` (CSS), y el JS
   sólo empuja `scrollLeft`, marca `.active` en los puntos y conmuta
   `.disabled` / `visibility` en las flechas.

   Breakpoints de PageFly — copiados del CSS del sitio, con decimales:
       mobile  <= 767.4999px       -> --ss-xs / --ws-xs
       tablet  767.5 – 1024.4999   -> --ss-sm / --ws-sm
       laptop  1024.5 – 1199.4999  -> --ss-md / --ws-md
       all     >= 1199.5px         -> --ss-lg / --ws-lg
   ========================================================================== */
(function () {
  'use strict';

  var BP = [
    { key: 'mobile', css: 'xs', mq: '(max-width: 767.4999px)' },
    { key: 'tablet', css: 'sm', mq: '(min-width: 767.5px) and (max-width: 1024.4999px)' },
    { key: 'laptop', css: 'md', mq: '(min-width: 1024.5px) and (max-width: 1199.4999px)' },
    { key: 'all',    css: 'lg', mq: '(min-width: 1199.5px)' }
  ];

  function currentBP() {
    for (var i = 0; i < BP.length; i++) if (window.matchMedia(BP[i].mq).matches) return BP[i];
    return BP[BP.length - 1];
  }

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  /* Desplazamiento por scrollLeft directo con rAF.
     ⚠️ En este tema, scrollBy()/scrollTo() (con behavior 'smooth' o 'auto') son NO-OP sobre el
     hero — hay un polyfill de smooth-scroll que los rompe —, mientras que asignar `scrollLeft`
     sí funciona. Por eso las flechas / puntos / autoplay no movían el banner. Además el CSS pone
     `scroll-snap-type: x mandatory !important`, que atrapa el scroll programático: lo desactivamos
     (con !important) sólo durante la animación y restauramos el valor inline previo al terminar,
     de modo que aterrizamos exactos en la diapositiva sin re-atrapar. */
  function animate(el, target) {
    if (el.__pfAnim) { cancelAnimationFrame(el.__pfAnim); el.__pfAnim = null; }
    if (el.__djimxAnim) { cancelAnimationFrame(el.__djimxAnim); el.__djimxAnim = null; }
    var pB = el.style.getPropertyValue('scroll-behavior'), pBp = el.style.getPropertyPriority('scroll-behavior');
    var pS = el.style.getPropertyValue('scroll-snap-type'), pSp = el.style.getPropertyPriority('scroll-snap-type');
    el.style.setProperty('scroll-behavior', 'auto', 'important');
    el.style.setProperty('scroll-snap-type', 'none', 'important');
    function restore() {
      if (pB) el.style.setProperty('scroll-behavior', pB, pBp); else el.style.removeProperty('scroll-behavior');
      if (pS) el.style.setProperty('scroll-snap-type', pS, pSp); else el.style.removeProperty('scroll-snap-type');
    }
    var max = el.scrollWidth - el.clientWidth;
    target = Math.max(0, Math.min(max, target));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.scrollLeft = target; restore(); return; }
    var start = el.scrollLeft, dist = target - start, t0 = performance.now(), dur = 420;
    var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
    (function tick(now) {
      var p = Math.min((now - t0) / dur, 1);
      el.scrollLeft = start + dist * ease(p);
      if (p < 1) el.__pfAnim = requestAnimationFrame(tick);
      else { el.__pfAnim = null; restore(); }
    })(t0);
  }

  /* PageFly serializa data-slider con comillas simples */
  function parseDataSlider(el) {
    var raw = el.getAttribute('data-slider');
    if (!raw) return null;
    try { return JSON.parse(raw.replace(/'/g, '"')); } catch (e) { return null; }
  }

  /* clase pf-NNN_ del contenedor (la que usa elementData) */
  function pfKey(el) {
    if (!el) return null;
    var m = (el.className || '').match(/\bpf-[A-Za-z0-9]*-?\d+_/);
    return m ? m[0] : null;
  }

  // ------------------------------------------------------------------ Slider
  function Slider(track, owner) {
    this.el = track;
    this.owner = owner;
    this.key = pfKey(owner);
    this.data = (window.PF_ELEMENT_DATA || {})[this.key] || null;
    this.cfg = parseDataSlider(track) || {};
    this.arrowPolicy = (window.PF_ARROWS || {})[this.key] || 'edge';
    this.wantDots = !!(window.PF_DOTS || {})[this.key];

    this.slides = Array.prototype.filter.call(track.children, function (c) {
      return c.classList.contains('pf-slide') || c.classList.contains('pf-slide-main-media');
    });
    this.prev = track.querySelector(':scope > .pf-slider-prev');
    this.next = track.querySelector(':scope > .pf-slider-next');
    this.nav  = track.querySelector(':scope > .pf-slider-nav');
    if (!this.nav && owner) this.nav = owner.querySelector('.pf-slider-nav');

    this.loop = !!(this.data && this.data.loop);
    this.bind();
    this.refresh();
    this.startAuto();
  }

  Slider.prototype.metrics = function () {
    var bp = currentBP();
    var cs = getComputedStyle(this.el);
    var ss = num(cs.getPropertyValue('--ss-' + bp.css), 1);   /* diapositivas por vista */
    var ws = num(cs.getPropertyValue('--ws-' + bp.css), 0);   /* item parcial (× 100)   */
    var toScroll = ss;
    if (this.cfg.slidesToScroll && this.cfg.slidesToScroll[bp.key] != null) toScroll = this.cfg.slidesToScroll[bp.key];
    else if (this.data && this.data.slidesToScroll && this.data.slidesToScroll[bp.key] != null) toScroll = this.data.slidesToScroll[bp.key];
    var first = this.slides[0];
    var step = first ? first.getBoundingClientRect().width : this.el.clientWidth;
    return { perView: ss + ws * 0.01, ss: ss, toScroll: Math.max(1, toScroll), step: step, total: this.slides.length };
  };

  Slider.prototype.pageCount = function (m) {
    var visible = Math.max(1, Math.floor(m.ss));
    return Math.max(1, Math.ceil((m.total - visible) / m.toScroll) + 1);
  };

  Slider.prototype.buildDots = function (pages) {
    if (!this.nav || !this.wantDots) return;
    if (this.nav.children.length === pages) return;
    this.nav.innerHTML = '';
    for (var i = 0; i < pages; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Ir a la diapositiva ' + (i + 1));
      b.dataset.page = String(i);
      this.nav.appendChild(b);
    }
  };

  Slider.prototype.refresh = function () {
    this.buildDots(this.pageCount(this.metrics()));
    this.update();
  };

  Slider.prototype.update = function () {
    var el = this.el;
    var m = this.metrics();
    var max = el.scrollWidth - el.clientWidth;
    var noOverflow = max <= 1;
    var atStart = el.scrollLeft <= 1;
    var atEnd = el.scrollLeft >= max - 1;

    var prevOff, nextOff;
    if (this.arrowPolicy === 'never') { prevOff = nextOff = true; }
    else if (this.arrowPolicy === 'always' && !noOverflow) { prevOff = nextOff = false; }
    else { prevOff = noOverflow || atStart; nextOff = noOverflow || atEnd; }

    if (this.prev) {
      this.prev.classList.toggle('disabled', prevOff);
      this.prev.style.visibility = prevOff ? 'hidden' : 'visible';
    }
    if (this.next) {
      this.next.classList.toggle('disabled', nextOff);
      this.next.style.visibility = nextOff ? 'hidden' : 'visible';
    }
    if (this.nav && this.nav.children.length) {
      var last = this.nav.children.length - 1;
      var page = m.step ? Math.round(el.scrollLeft / (m.step * m.toScroll)) : 0;
      if (atEnd) page = last;
      page = Math.min(last, Math.max(0, page));
      for (var i = 0; i <= last; i++) this.nav.children[i].classList.toggle('active', i === page);
    }
  };

  Slider.prototype.go = function (dir) {
    var el = this.el, m = this.metrics();
    var max = el.scrollWidth - el.clientWidth;
    var step = m.step * m.toScroll;
    if (this.loop) {
      if (dir > 0 && el.scrollLeft >= max - 1) { animate(el, 0); return; }
      if (dir < 0 && el.scrollLeft <= 1) { animate(el, max); return; }
    }
    /* ⚠️ Basar el destino en el índice de diapositiva MÁS CERCANO, no en scrollLeft crudo:
       si el autoplay dispara mientras otra animación va a medias, "scrollLeft + step" daría
       un destino fuera de borde y el banner se quedaba "a mitad". */
    var cur = step ? Math.round(el.scrollLeft / step) : 0;
    animate(el, Math.max(0, Math.min(max, (cur + dir) * step)));
  };

  Slider.prototype.goToPage = function (page) {
    var m = this.metrics();
    animate(this.el, page * m.step * m.toScroll);
  };

  // ------------------------------------------------------------ autoplay
  /* Solo el banner (hero escritorio + móvil) hace autoplay. Catnav, listas de
     productos, galerías de tarjeta y cualquier otro .scrollfix quedan manuales. */
  var HERO_AUTOPLAY_KEYS = {
    'pf-e3488b4f18bb-11_': true, /* banner desktop */
    'pf-e32585fcdd9d-10_': true  /* banner mobile  */
  };

  Slider.prototype.startAuto = function () {
    var d = this.data || {};
    var delay = d.autoPlayDelay, auto = false, pauseHover = d.pauseOnHover;
    var isHeroBanner = !!(this.key && HERO_AUTOPLAY_KEYS[this.key]);
    if (isHeroBanner) {
      delay = window.__djimxHeroAutoplayMs || delay || 9000;
      auto = true;
      if (d.pauseOnHover == null) pauseHover = true;
    }
    if (!auto || !delay) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var self = this;
    this.timer = setInterval(function () {
      if (self.paused) return;
      if (document.hidden) return;
      self.go(1);
    }, delay);

    if (pauseHover) {
      var host = this.owner || this.el;
      host.addEventListener('mouseenter', function () { self.paused = true; });
      host.addEventListener('mouseleave', function () { self.paused = false; });
    }
  };

  Slider.prototype.bind = function () {
    var self = this;
    function nav(e, dir) { e.preventDefault(); e.stopPropagation(); self.paused = true; self.go(dir); setTimeout(function () { self.paused = false; }, 1500); }
    if (this.prev) this.prev.addEventListener('click', function (e) { nav(e, -1); });
    if (this.next) this.next.addEventListener('click', function (e) { nav(e, 1); });
    if (this.nav) this.nav.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-page]');
      if (!b) return;
      e.preventDefault(); e.stopPropagation();
      self.paused = true;
      self.goToPage(parseInt(b.dataset.page, 10));
      setTimeout(function () { self.paused = false; }, 1500);
    });
    var raf = null;
    this.el.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; self.update(); });
    }, { passive: true });
  };

  // ---------------------------------------------------------------- arranque
  function init() {
    var instances = [];

    /* 1 — sliders con marcado servido por el sitio (portada, barra de
           categorías, listas de 4 productos) */
    document.querySelectorAll('.pf-slider').forEach(function (track) {
      instances.push(new Slider(track, track.parentElement));
    });

    /* 2 — galerías de producto: PageFly inyecta sus flechas en tiempo de
           ejecución; el CSS del sitio ya las dimensiona con la container
           query `@container main-media`. */
    document.querySelectorAll('.pf-media-slider').forEach(function (track) {
      var main = track.closest('.pf-main-media');
      var key = pfKey(main);
      var conf = (window.PF_ELEMENT_DATA || {})[key];
      var style = (conf && conf.navStyle) || 'nav-style-2';
      if (style !== 'none' && !track.querySelector(':scope > .pf-slider-next')) {
        ['prev', 'next'].forEach(function (dir) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'pf-slider-' + dir + ' ' + style;
          b.setAttribute('aria-label', dir === 'prev' ? 'Anterior' : 'Siguiente');
          track.appendChild(b);
        });
      }
      instances.push(new Slider(track, main));
    });

    var t = null;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { instances.forEach(function (s) { s.refresh(); }); }, 120);
    });
    window.addEventListener('load', function () { instances.forEach(function (s) { s.refresh(); }); });
    window.__pfSliders = instances;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
